import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import { ChevronLeft, MousePointer2, ListFilter, ArrowRight, Search } from 'lucide-react';
import {
  DndContext,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import styles from './CategoryGames.module.scss';
import { GameCategory } from './CategoryItem';
import GameCard, { Game } from './GameCard';
import ConfirmDeleteModal from 'components/modals/ConfirmDeleteModal';
import { gameCategoryService } from 'components/api/gameCategories';

const MIN_CAROUSEL_GAMES = 12;
const MAX_CAROUSEL_GAMES = 16;

interface FirstPageGamesProps {
  category: GameCategory;
  onBack: () => void;
}

const DroppableZone: React.FC<{ id: string; children: React.ReactNode; className?: string }> = ({
  id,
  children,
  className,
}) => {
  const { setNodeRef } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={className} id={id}>
      {children}
    </div>
  );
};

const FirstPageGames: React.FC<FirstPageGamesProps> = ({ category, onBack }) => {
  const dispatch = useAppDispatch();
  const { id: siteId } = useParams<{ id: string }>();

  // Left panel: available games from API
  const [availableGames, setAvailableGames] = useState<Game[]>([]);
  const [availablePage, setAvailablePage] = useState(1);
  const [availableTotal, setAvailableTotal] = useState(0);
  const [isAvailableLoading, setIsAvailableLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Right panel: carousel games
  const [categoryGames, setCategoryGames] = useState<Game[]>([]);
  const [isCatLoading, setIsCatLoading] = useState(false);
  const [rightLimit, setRightLimit] = useState(15);

  const [selectedGameIds, setSelectedGameIds] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: '', title: '' });
  const [warningModal, setWarningModal] = useState({ open: false, title: '', message: '' });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const fetchAvailableGames = useCallback(
    async (page: number, append = false, searchStr = '') => {
      try {
        setIsAvailableLoading(true);
        const res = await gameCategoryService.getGamesByCategory({
          categoryId: category.id,
          page,
          pageSize: 16,
          siteId,
          search: searchStr,
        });
        const items = res?.data?.items || [];
        const total = res?.data?.total || 0;

        const formatted: Game[] = items.map((item: any) => {
          const game = item.game || item;
          const gameId = item.gameId || game.id || item.id;
          return {
            id: String(gameId),
            title: game.title || '',
            provider: game.producer || game.provider || '',
            image: game.imageUrl || game.image || '',
          };
        });

        if (append) {
          setAvailableGames((prev) => {
            const existing = new Set(prev.map((g) => g.id));
            return [...prev, ...formatted.filter((g) => !existing.has(g.id))];
          });
        } else {
          setAvailableGames(formatted);
        }
        setAvailableTotal(total);
      } catch (err) {
        console.error('Failed to load available games for carousel', err);
      } finally {
        setIsAvailableLoading(false);
      }
    },
    [category.id, siteId],
  );

  useEffect(() => {
    setAvailablePage(1);
    fetchAvailableGames(1, false, debouncedSearchQuery);
  }, [debouncedSearchQuery, fetchAvailableGames]);

  useEffect(() => {
    const fetchCollectionGames = async () => {
      try {
        setIsCatLoading(true);
        const res = await gameCategoryService.getCollectionGames(category.id);
        const items = res?.data?.items || res?.data || [];
        const formatted: Game[] = (Array.isArray(items) ? items : []).map((item: any) => {
          const game = item.game || item;
          const gameId = item.gameId || game.id || item.id;
          return {
            id: String(gameId),
            title: game.title || '',
            provider: game.producer || game.provider || '',
            image: game.imageUrl || game.image || '',
          };
        });
        setCategoryGames(formatted);
      } catch (err) {
        console.error('Failed to load collection games', err);
      } finally {
        setIsCatLoading(false);
      }
    };
    fetchCollectionGames();
  }, [category.id]);

  const handleLoadMoreAvailable = () => {
    const nextPage = availablePage + 1;
    setAvailablePage(nextPage);
    fetchAvailableGames(nextPage, true, debouncedSearchQuery);
  };

  // Available games with disabled flag for already added ones
  const availableGamesWithDisabled = useMemo(() => {
    return availableGames.map((game) => ({
      ...game,
      dndId: `avail-${game.id}`,
      isDisabled: !!categoryGames.find((cg) => cg.id === game.id),
    }));
  }, [availableGames, categoryGames]);

  const displayedCategoryGames = useMemo(() => {
    return categoryGames.slice(0, rightLimit);
  }, [categoryGames, rightLimit]);

  const handleSelectGame = (id: string) => {
    const newSelected = new Set(selectedGameIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedGameIds(newSelected);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeDndId = active.id as string;
    const overDndId = over.id as string;

    // 1. Reordering within Carousel (Right Panel)
    if (activeDndId.startsWith('cat-') && overDndId.startsWith('cat-')) {
      if (activeDndId !== overDndId) {
        setCategoryGames((items) => {
          const oldIndex = items.findIndex((i) => `cat-${i.id}` === activeDndId);
          const newIndex = items.findIndex((i) => `cat-${i.id}` === overDndId);
          setHasChanges(true);
          return arrayMove(items, oldIndex, newIndex);
        });
      }
      return;
    }

    // 2. Dragging from Available (Left Panel) to Carousel (Right Panel)
    const isOverRightPanel = overDndId === 'right-panel' || overDndId.startsWith('cat-');

    if (isOverRightPanel && activeDndId.startsWith('avail-')) {
      const activeGameId = activeDndId.replace('avail-', '');
      const activeGame = availableGames.find((g) => g.id === activeGameId);
      if (!activeGame) return;

      const gamesToAdd = selectedGameIds.has(activeGameId)
        ? availableGames.filter((g) => selectedGameIds.has(g.id))
        : [activeGame];

      // Filter out games already in category to avoid duplicates
      const newGames = gamesToAdd.filter((g) => !categoryGames.find((cg) => cg.id === g.id));

      if (newGames.length > 0) {
        if (categoryGames.length + newGames.length > MAX_CAROUSEL_GAMES) {
          setWarningModal({
            open: true,
            title: 'Carousel Limit Reached',
            message: `You cannot add ${newGames.length} game(s) to this carousel. The carousel is limited to a maximum of ${MAX_CAROUSEL_GAMES} games. Please delete some existing games from the right-hand panel first before dragging new ones.`,
          });
          return;
        }

        setCategoryGames((prev) => [...newGames, ...prev]);
        setSelectedGameIds(new Set());
        setHasChanges(true);
      }
    }
  };

  const handleRemoveGame = (id: string) => {
    const game = categoryGames.find((g) => g.id === id);
    if (game) {
      setDeleteModal({ open: true, id, title: game.title });
    }
  };

  const confirmRemoveGame = () => {
    setCategoryGames(categoryGames.filter((g) => g.id !== deleteModal.id));
    setHasChanges(true);
    setDeleteModal((prev) => ({ ...prev, open: false }));
  };

  const handleSaveOrder = async () => {
    try {
      setIsSaving(true);
      const gameIds = categoryGames.map((cg) => {
        const parsed = Number(cg.id);
        return isNaN(parsed) ? cg.id : parsed;
      });

      if (gameIds.length < MIN_CAROUSEL_GAMES || gameIds.length > MAX_CAROUSEL_GAMES) {
        dispatch(
          showToast({
            text: `Please add between ${MIN_CAROUSEL_GAMES} and ${MAX_CAROUSEL_GAMES} games to the carousel (currently: ${gameIds.length}).`,
            type: 'error',
          }),
        );
        return;
      }

      await gameCategoryService.updateCollection(category.id, gameIds);
      setHasChanges(false);
      dispatch(showToast({ text: 'Carousel games saved successfully!', type: 'success' }));
    } catch (error) {
      console.error('Failed to save carousel games:', error);
      dispatch(showToast({ text: 'Failed to save carousel games', type: 'error' }));
    } finally {
      setIsSaving(false);
    }
  };

  const activeGame = useMemo(() => {
    if (!activeId) return null;
    const actualId = activeId.replace('avail-', '').replace('cat-', '');
    return (
      categoryGames.find((g) => g.id === actualId) ||
      availableGames.find((g) => g.id === actualId) ||
      null
    );
  }, [activeId, categoryGames, availableGames]);

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Box className={styles.backBtn} onClick={onBack}>
          <ChevronLeft size={28} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
          <Typography variant="h5" className={styles.title}>
            Carousel Games ({MIN_CAROUSEL_GAMES}-{MAX_CAROUSEL_GAMES}) - {category.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
            This feature will allow you to curate {MIN_CAROUSEL_GAMES} to {MAX_CAROUSEL_GAMES} games
            specifically for this category&apos;s home page carousel.
          </Typography>
        </Box>
        {hasChanges && (
          <Button
            variant="contained"
            className={styles.saveOrderBtn}
            onClick={handleSaveOrder}
            disabled={isSaving}
            sx={{ ml: 'auto' }}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isSaving ? 'Saving...' : 'Save Order'}
          </Button>
        )}
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Box className={styles.mainLayout}>
          {/* Left Panel: Available Games */}
          <Box className={styles.panel}>
            <Box className={styles.panelHeader}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  mb: 1,
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ListFilter size={18} color="#8698a6" />
                  <Typography variant="h6" className={styles.panelTitle}>
                    Available Games
                  </Typography>
                </Box>
                <TextField
                  size="small"
                  placeholder="Search game..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={16} color="#8698a6" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: '180px',
                    '& .MuiOutlinedInput-root': {
                      color: 'var(--text-primary)',
                      backgroundColor: 'var(--bg-input)',
                      fontSize: '13px',
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.1)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'var(--accent-blue)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'var(--accent-blue)',
                      },
                    },
                    '& .MuiInputBase-input': {
                      padding: '6px 8px 6px 0',
                    },
                  }}
                />
              </Box>
            </Box>
            <Box className={styles.panelContent}>
              <SortableContext
                items={availableGamesWithDisabled.filter((g) => !g.isDisabled).map((g) => g.dndId)}
                strategy={verticalListSortingStrategy}
              >
                {availableGamesWithDisabled.map((game) => (
                  <GameCard
                    key={game.dndId}
                    game={game}
                    dndId={game.dndId}
                    isSelected={selectedGameIds.has(game.id)}
                    onSelect={handleSelectGame}
                    isMultiSelectMode={true}
                    isSortable={!game.isDisabled}
                    isDisabled={game.isDisabled}
                  />
                ))}
              </SortableContext>

              {isAvailableLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} sx={{ color: 'var(--accent-blue)' }} />
                </Box>
              )}

              {availableGames.length < availableTotal && !isAvailableLoading && (
                <Button className={styles.loadMoreBtn} onClick={handleLoadMoreAvailable}>
                  Load More ({availableTotal - availableGames.length} left)
                </Button>
              )}

              {availableGamesWithDisabled.length === 0 && !isAvailableLoading && (
                <Box className={styles.emptyState}>
                  <MousePointer2 size={40} opacity={0.3} />
                  <Typography variant="body2">No more games matching filter.</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Right Panel: Carousel Games */}
          <DroppableZone id="right-panel" className={styles.panel}>
            <Box className={styles.panelHeader}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ArrowRight size={18} color="var(--accent-blue)" />
                <Typography variant="h6" className={styles.panelTitle}>
                  Games in Carousel
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: '#8698a6' }}>
                {categoryGames.length} / {MAX_CAROUSEL_GAMES} games (min {MIN_CAROUSEL_GAMES}). Drag
                to reorder.
              </Typography>
            </Box>
            <Box className={styles.panelContent}>
              <SortableContext
                items={categoryGames.map((g) => `cat-${g.id}`)}
                strategy={verticalListSortingStrategy}
              >
                {displayedCategoryGames.map((game) => (
                  <GameCard
                    key={game.id}
                    game={game}
                    dndId={`cat-${game.id}`}
                    isSortable={true}
                    onRemove={handleRemoveGame}
                  />
                ))}
              </SortableContext>
              {isCatLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} sx={{ color: 'var(--accent-blue)' }} />
                </Box>
              )}
              {categoryGames.length > rightLimit && (
                <Button
                  className={styles.loadMoreBtn}
                  onClick={() => setRightLimit((prev) => prev + 15)}
                >
                  Load More ({categoryGames.length - rightLimit} left)
                </Button>
              )}
              {categoryGames.length === 0 && !isCatLoading && (
                <Box className={styles.emptyState}>
                  <Box sx={{ mb: 1 }}>🎯</Box>
                  <Typography variant="body2">Drag games here to add to carousel.</Typography>
                </Box>
              )}
            </Box>
          </DroppableZone>
        </Box>

        <DragOverlay dropAnimation={null}>
          {activeGame ? (
            <Box sx={{ pointerEvents: 'none' }}>
              <GameCard
                game={activeGame}
                dndId={activeId as string}
                isSortable={true}
                isSelected={selectedGameIds.has(activeGame.id)}
                isMultiSelectMode={selectedGameIds.size > 1}
              />
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Confirmation & Warning Dialogs */}
      <ConfirmDeleteModal
        open={deleteModal.open}
        title={deleteModal.title}
        onClose={() => setDeleteModal({ ...deleteModal, open: false })}
        onConfirm={confirmRemoveGame}
      />

      <Dialog
        open={warningModal.open}
        onClose={() => setWarningModal({ ...warningModal, open: false })}
        PaperProps={{
          sx: {
            backgroundColor: 'var(--bg-panel)',
            color: 'var(--text-primary)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            p: 2,
            maxWidth: '400px',
          },
        }}
      >
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'center',
            textAlign: 'center',
            p: 2,
          }}
        >
          <Box sx={{ fontSize: '40px' }}>⚠️</Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary)' }}>
            {warningModal.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {warningModal.message}
          </Typography>
          <Button
            variant="contained"
            onClick={() => setWarningModal({ ...warningModal, open: false })}
            sx={{
              backgroundColor: 'var(--accent-blue)',
              color: 'white',
              textTransform: 'none',
              borderRadius: '8px',
              mt: 2,
              px: 4,
              '&:hover': {
                backgroundColor: 'var(--accent-blue-hover)',
              },
            }}
          >
            Acknowledge
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FirstPageGames;
